/* eslint-disable @typescript-eslint/no-explicit-any */
import { requestExportExcelReport14 } from 'app/api/report'
import { ReportMessageCode } from 'app/api/report/constant'
import {
  GenderReport14Enum,
  IFilterReport14,
  IParamsGetReport14,
  ServiceDataReport14,
  TypeReport14Enum,
} from 'app/api/report/model/report-14'
import R from 'app/assets/R'
import { getTotalWeekNumbersOfMonth } from 'app/common/helpers'
import {
  ExpandedTableValue,
  RenderValueTableReport,
  convertColumnTable,
} from 'app/components/tables/BaseTableReport/hook'
import {
  IBaseColumnReport,
  IOnCell,
} from 'app/components/tables/BaseTableReport/type'
import { getArray } from 'app/components/tables/common-table/constant'
import { notificationController } from 'app/controllers/notification-controller'
import { convertToTitleCase } from 'app/hook'
import { Pagination, ResponseType, moment } from 'parkway-web-common'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IDataReport014 } from './custom-hook/data'
import { AgeGroupEnum, GenderEnum } from './enum'
moment.locale('en', {
  week: {
    dow: 1,
  },
})

export const useCommonHook = ({
  expandedRowKeys,
  typeHook,
  handleExpand,
  currentFilter,
  fetchLoading,
}: {
  expandedRowKeys: number[]
  fetchLoading: (value: boolean) => void
  typeHook: TypeReport14Enum
  fetch: (pagination: Pagination, params?: IFilterReport14) => void
  handleExpand: (isExpanded: boolean, key?: number) => void
  currentFilter?: IFilterReport14
}) => {
  const { t } = useTranslation()
  const [isLoadingExport, setIsLoadingExport] = useState(false)

  const exportExcel = async () => {
    try {
      if (!currentFilter?.year) return
      setIsLoadingExport(true)

      const ageData = convertAgeGroupToArrayAge(currentFilter?.age)
      let params: IParamsGetReport14 = {
        year: Number(currentFilter?.year),
        patientGender: convertGenderToParams(currentFilter?.gender),
        location: currentFilter?.clinic,
        patientAgeFrom: ageData?.patientAgeFrom,
        patientAgeTo: ageData?.patientAgeTo,
      }

      switch (typeHook) {
        case TypeReport14Enum.CASE:
          params = {
            ...params,
            exportType: 'CASE',
          }
          break

        case TypeReport14Enum.REVENUE:
          params = {
            ...params,
            exportType: 'REVENUE',
          }
          break
      }
      fetchLoading(true)
      const res: ResponseType<{ url: string }> =
        await requestExportExcelReport14(params)

      if (res?.msgcode === ReportMessageCode.Report14.successGetData) {
        window.open(res?.data?.url ?? '')
        notificationController.success({
          message: t(R.strings.download_excel_success),
        })
      }
    } catch (error: any) {
      console.log(error)
      notificationController.error(error)
    } finally {
      fetchLoading(false)
      setIsLoadingExport(false)
    }
  }

  function getWeeksInMonth(year: number, month: number): number {
    return getTotalWeekNumbersOfMonth(year, month)
  }

  const columns = useMemo(() => {
    type TypeColumn = IDataReport014

    const onCellGroupService = (record: TypeColumn): IOnCell => {
      if (record?.isTotalTable && expandedRowKeys.length) return { colSpan: 2 }

      if (record?.isTotalTable && !expandedRowKeys.length) return { colSpan: 1 }

      const isExpanded = expandedRowKeys.includes(record?.key ?? 0)
      if (!isExpanded && record?.children && expandedRowKeys?.length) {
        return { rowSpan: 1, colSpan: 2 }
      }

      if (!isExpanded && record?.children) {
        return { rowSpan: 1 }
      }

      if (isExpanded && !record?.children?.length) {
        return { rowSpan: 1 }
      }

      return {
        rowSpan: record.children?.length ? record.children?.length + 1 : 0,
      }
    }

    const onCellService = (record: TypeColumn): IOnCell => {
      if (record?.isTotalTable) {
        return { colSpan: 0 }
      }
      const isExpanded = expandedRowKeys.includes(record.key ?? 0)

      if (!isExpanded && record?.children && !!expandedRowKeys?.length) {
        return { colSpan: 0 }
      }
      return {}
    }

    let options: IBaseColumnReport<TypeColumn> = [
      convertColumnTable<TypeColumn>({
        title: t(R.strings.group_service),
        key: 'groupService',
        onCell: onCellGroupService,
        fixed: 'left',
        classNameWidthColumnOverwrite: 'very-big-column',
        render: (_, record) => {
          if (record?.isTotalTable) {
            return (
              <ExpandedTableValue
                value={convertToTitleCase(record?.groupService ?? '')}
                isTotalTable
              />
            )
          }

          const isExpanded = expandedRowKeys.includes(record.key ?? 0)

          if (!record?.children) return null

          const _handleExpand = () => {
            return handleExpand(!isExpanded, record?.key)
          }

          return (
            <ExpandedTableValue
              isExpanded={isExpanded}
              handleExpand={_handleExpand}
              value={convertToTitleCase(record?.groupService ?? '')}
            />
          )
        },
      }),
    ]

    if (expandedRowKeys?.length) {
      options = options?.concat([
        convertColumnTable<TypeColumn>({
          title: t(R.strings.service),
          key: 'service',
          onCell: onCellService,
          fixed: 'left',
          classNameWidthColumnOverwrite: 'very-big-column',
          render: (_, record) => {
            return (
              <RenderValueTableReport
                record={record}
                valueString={record?.service}
                expandedRowKeys={expandedRowKeys}
                justify="start"
              />
            )
          },
        }),
      ])
    }

    const months = getArray(12)?.map(month => {
      const numberWeeksOfMonth = getWeeksInMonth(
        +(currentFilter?.year ?? moment().year),
        month,
      )
      return {
        month,
        numberWeeksOfMonth,
      }
    })

    const monthsOptions = months?.map(month => {
      const weeksOfMonth = getArray(month?.numberWeeksOfMonth)?.map(itm => {
        return convertColumnTable<any>({
          title: t(R.strings.week_no, { week: itm }),
          key: `M${month.month}W${itm}`,
          render: (_, record) => {
            return RenderValueTableReport({
              record,
              firstLevelValue: record?.[`M${month.month}W${itm}${typeHook}`],
              secondLevelValue:
                record?.[`M${month.month}W${itm}Total${typeHook}`],
              isFillBlueInTotal: true,
              expandedRowKeys,
              percent: record?.[`M${month.month}W${itm}Percent${typeHook}`],
              percentTotal:
                record?.[`M${month.month}W${itm}PercentTotal${typeHook}`],
              isShowPercent: true,
              isShowValueUpAndDown: true,
              prefix: typeHook === TypeReport14Enum.CASE ? '' : undefined,
              decimal: 2,
            })
          },
        })
      })
      return {
        title: t(R.strings.month_no, { month: month?.month }),
        children: weeksOfMonth,
      }
    })

    const monthsTotal = getArray(12).map(itm => {
      return convertColumnTable<any>({
        title: t(R.strings.month_no, { month: itm }),
        key: `M${itm}`,
        render: (_, record) => {
          return RenderValueTableReport({
            record,
            firstLevelValue: record?.[`M${itm}${typeHook}`],
            secondLevelValue: record?.[`M${itm}Total${typeHook}`],
            isFillBlueInTotal: true,
            expandedRowKeys,
            percent: record?.[`M${itm}Percent${typeHook}`],
            percentTotal: record?.[`M${itm}PercentTotal${typeHook}`],
            isShowPercent: true,
            isShowValueUpAndDown: true,
            prefix: typeHook === TypeReport14Enum.CASE ? '' : undefined,
            decimal: 2,
          })
        },
      })
    })

    const quartersTotal = getArray(4).map(itm => {
      return convertColumnTable<any>({
        title: t(R.strings.quarter_num, { quarter: itm }),
        key: `Q${itm}`,
        render: (_, record) => {
          return RenderValueTableReport({
            record,
            firstLevelValue: record?.[`Q${itm}${typeHook}`],
            secondLevelValue: record?.[`Q${itm}Total${typeHook}`],
            isFillBlueInTotal: true,
            expandedRowKeys,
            percent: record?.[`Q${itm}Percent${typeHook}`],
            percentTotal: record?.[`Q${itm}PercentTotal${typeHook}`],
            isShowPercent: true,
            isShowValueUpAndDown: true,
            prefix: typeHook === TypeReport14Enum.CASE ? '' : undefined,
            decimal: 2,
          })
        },
      })
    })

    return [
      ...options,
      {
        title:
          typeHook === TypeReport14Enum.CASE
            ? t(R.strings.report_14_content_number_of_case)
            : t(R.strings?.revenue),
        children: [
          ...monthsOptions,
          ...monthsTotal,
          ...quartersTotal,
          convertColumnTable<any>({
            title: t(R.strings.year_number, { year: currentFilter?.year }),
            key: `Y${currentFilter?.year}`,
            render: (_, record) => {
              return RenderValueTableReport({
                record,
                firstLevelValue: record?.[`Y${currentFilter?.year}${typeHook}`],
                secondLevelValue:
                  record?.[`Y${currentFilter?.year}Total${typeHook}`],
                isFillBlueInTotal: true,
                expandedRowKeys,
                percent: record?.[`Y${currentFilter?.year}Percent${typeHook}`],
                percentTotal:
                  record?.[`Y${currentFilter?.year}Percent${typeHook}`],
                isShowPercent: true,
                isShowValueUpAndDown: true,
                prefix: typeHook === TypeReport14Enum.CASE ? '' : undefined,
                decimal: 2,
              })
            },
          }),
        ],
      },
    ]
  }, [t, expandedRowKeys, typeHook, currentFilter])

  return {
    columns,
    exportExcel,
    isLoadingExport,
  }
}

export const convertDataApiToDataTableReport14Case = ({
  serviceData,
  type,
}: {
  serviceData: ServiceDataReport14
  type: 'total' | 'child'
}) => {
  let dataObjectTable: any = {}

  const months = serviceData.months

  const quarters = serviceData?.quarters

  const years = serviceData?.years

  const monthsOfMonthKeys = Object?.keys(months)
  switch (type) {
    case 'total':
      monthsOfMonthKeys?.forEach(keyOfMonth => {
        const monthData = serviceData?.months?.[keyOfMonth]
        const weekData = monthData?.weeks
        for (const week in weekData) {
          const weekDetail = weekData?.[week]
          dataObjectTable = {
            ...dataObjectTable,
            [`M${keyOfMonth}W${week}TotalCase`]: weekDetail?.case ?? 0,
            [`M${keyOfMonth}W${week}PercentTotalCase`]: weekDetail?.casePercent ?? 0,
          }
        }
        dataObjectTable = {
          ...dataObjectTable,
          [`M${keyOfMonth}TotalCase`]: monthData?.case ?? 0,
          [`M${keyOfMonth}PercentTotalCase`]: monthData?.casePercent ?? 0,
        }
      })

      for (const quarter in quarters) {
        const quarterDetail = quarters?.[quarter]
        dataObjectTable = {
          ...dataObjectTable,
          [`Q${quarter}TotalCase`]: quarterDetail?.case ?? 0,
          [`Q${quarter}PercentTotalCase`]: quarterDetail?.casePercent ?? 0,
        }
      }

      for (const year in years) {
        const yearDetail = years?.[year]
        dataObjectTable = {
          ...dataObjectTable,
          [`Y${year}TotalCase`]: yearDetail?.case ?? 0,
          [`Y${year}PercentTotalCase`]: yearDetail?.casePercent ?? 0,
        }
      }
      return dataObjectTable
    case 'child':
      monthsOfMonthKeys?.forEach(keyOfMonth => {
        const monthData = serviceData?.months?.[keyOfMonth]
        const weekData = monthData?.weeks
        for (const week in weekData) {
          const weekDetail = weekData?.[week]
          dataObjectTable = {
            ...dataObjectTable,
            [`M${keyOfMonth}W${week}Case`]: weekDetail?.case ?? 0,
            [`M${keyOfMonth}W${week}PercentCase`]: weekDetail?.casePercent ?? 0,
          }
        }
        dataObjectTable = {
          ...dataObjectTable,
          [`M${keyOfMonth}Case`]: monthData?.case ?? 0,
          [`M${keyOfMonth}PercentCase`]: monthData?.casePercent ?? 0,
        }
      })

      for (const quarter in quarters) {
        const quarterDetail = quarters[quarter]
        dataObjectTable = {
          ...dataObjectTable,
          [`Q${quarter}Case`]: quarterDetail?.case ?? 0,
          [`Q${quarter}PercentCase`]: quarterDetail?.casePercent ?? 0,
        }
      }

      for (const year in years) {
        const yearDetail = years[year]
        dataObjectTable = {
          ...dataObjectTable,
          [`Y${year}Case`]: yearDetail?.case ?? 0,
          [`Y${year}PercentCase`]: yearDetail?.casePercent ?? 0,
        }
      }
      return dataObjectTable
  }
}

export const convertDataApiToDataTableReport14Revenue = ({
  serviceData,
  type,
}: {
  serviceData: ServiceDataReport14
  type: 'total' | 'child'
}) => {
  let dataObjectTable: any = {}

  const months = serviceData?.months

  const quarters = serviceData?.quarters

  const years = serviceData?.years

  const monthsOfMonthKeys = Object?.keys(months)
  switch (type) {
    case 'total':
      monthsOfMonthKeys?.forEach(keyOfMonth => {
        const monthData = serviceData?.months?.[keyOfMonth]
        const weekData = monthData?.weeks
        for (const week in weekData) {
          const weekDetail = weekData?.[week]
          dataObjectTable = {
            ...dataObjectTable,
            [`M${keyOfMonth}W${week}TotalRevenue`]: weekDetail?.revenue ?? 0,
            [`M${keyOfMonth}W${week}PercentTotalRevenue`]:
              weekDetail?.revenuePercent ?? 0,
          }
        }
        dataObjectTable = {
          ...dataObjectTable,
          [`M${keyOfMonth}TotalRevenue`]: monthData?.revenue ?? 0,
          [`M${keyOfMonth}PercentTotalRevenue`]: monthData?.revenuePercent ?? 0,
        }
      })

      for (const quarter in quarters) {
        const quarterDetail = quarters?.[quarter]
        dataObjectTable = {
          ...dataObjectTable,
          [`Q${quarter}TotalRevenue`]: quarterDetail?.revenue ?? 0,
          [`Q${quarter}PercentTotalRevenue`]: quarterDetail?.revenuePercent ?? 0,
        }
      }

      for (const year in years) {
        const yearDetail = years[year]
        dataObjectTable = {
          ...dataObjectTable,
          [`Y${year}TotalRevenue`]: yearDetail?.revenue ?? 0,
          [`Y${year}PercentTotalRevenue`]: yearDetail?.revenuePercent ?? 0,
        }
      }
      return dataObjectTable
    case 'child':
      monthsOfMonthKeys?.forEach(keyOfMonth => {
        const monthData = serviceData?.months?.[keyOfMonth]
        const weekData = monthData?.weeks
        for (const week in weekData) {
          const weekDetail = weekData?.[week]
          dataObjectTable = {
            ...dataObjectTable,
            [`M${keyOfMonth}W${week}Revenue`]: weekDetail?.revenue ?? 0,
            [`M${keyOfMonth}W${week}PercentRevenue`]:
              weekDetail?.revenuePercent ?? 0,
          }
        }
        dataObjectTable = {
          ...dataObjectTable,
          [`M${keyOfMonth}Revenue`]: monthData?.revenue ?? 0,
          [`M${keyOfMonth}PercentRevenue`]: monthData?.revenuePercent ?? 0,
        }
      })

      for (const quarter in quarters) {
        const quarterDetail = quarters[quarter]
        dataObjectTable = {
          ...dataObjectTable,
          [`Q${quarter}Revenue`]: quarterDetail?.revenue ?? 0,
          [`Q${quarter}PercentRevenue`]: quarterDetail?.revenuePercent ?? 0,
        }
      }

      for (const year in years) {
        const yearDetail = years[year]
        dataObjectTable = {
          ...dataObjectTable,
          [`Y${year}Revenue`]: yearDetail?.revenue ?? 0,
          [`Y${year}PercentRevenue`]: yearDetail?.revenuePercent ?? 0,
        }
      }
      return dataObjectTable
  }
}

export const convertAgeGroupToArrayAge = (ageGroup?: AgeGroupEnum) => {
  switch (ageGroup) {
    case AgeGroupEnum.AGE_0_8:
      return {
        patientAgeFrom: 0,
        patientAgeTo: 8,
      }
    case AgeGroupEnum.AGE_9_14:
      return {
        patientAgeFrom: 9,
        patientAgeTo: 14,
      }
    case AgeGroupEnum.AGE_15_17:
      return {
        patientAgeFrom: 15,
        patientAgeTo: 17,
      }
    case AgeGroupEnum.AGE_18_23:
      return {
        patientAgeFrom: 18,
        patientAgeTo: 23,
      }
    case AgeGroupEnum.AGE_24_27:
      return {
        patientAgeFrom: 24,
        patientAgeTo: 27,
      }
    case AgeGroupEnum.AGE_28_34:
      return {
        patientAgeFrom: 28,
        patientAgeTo: 34,
      }
    case AgeGroupEnum.AGE_35_44:
      return {
        patientAgeFrom: 35,
        patientAgeTo: 44,
      }
    case AgeGroupEnum.AGE_45_54:
      return {
        patientAgeFrom: 45,
        patientAgeTo: 54,
      }
    case AgeGroupEnum.AGE_MORE_54:
      return {
        patientAgeFrom: 55,
      }
  }
}

export const convertGenderToParams = (gender?: GenderEnum) => {
  switch (gender) {
    case GenderEnum.MALE:
      return GenderReport14Enum.M
    case GenderEnum.FEMALE:
      return GenderReport14Enum.F
    case GenderEnum.OTHER:
      return GenderReport14Enum.Other
  }
}
